// ─── Payroll menu items (key matches PAYROLL_GUIDES below) ────────────────────

export const PAYROLL_ITEMS = [
  {
    key: 'allowances',
    label: 'Allowances',
    path: '/allowances',
    sidebar: 'Payroll → Allowances',
    access_points: ['allowances'],
  },
  {
    key: 'deductions',
    label: 'Deductions',
    path: '/deductions',
    sidebar: 'Payroll → Deductions',
    access_points: ['deductions'],
  },
  {
    key: 'payroll_profile',
    label: 'Payroll Profile',
    path: '/payroll_profile',
    sidebar: 'Payroll → Payroll Profile',
    access_points: ['payroll_profile'],
  },
  {
    key: 'employee_payroll',
    label: 'Employee Payroll',
    path: '/employee_payroll',
    sidebar: 'Payroll → Employee Payroll',
    access_points: ['employee_payroll'],
  },
  {
    key: 'designation_payroll',
    label: 'Designation Profile',
    path: '/designation_payroll',
    sidebar: 'Payroll → Designation Profile',
    access_points: ['designation_payroll'],
  },
  {
    key: 'payslip',
    label: 'Payslip',
    path: '/payslip',
    sidebar: 'Payroll → Payslip',
    access_points: ['payslip'],
  },
  {
    key: 'payroll_recalculation',
    label: 'Payroll Recalculation',
    path: '/payroll_recalculation',
    sidebar: 'Payroll → Payroll Recalculation',
    access_points: ['payroll_recalculation'],
  },
  {
    key: 'detailed_payroll',
    label: 'Detailed Payroll Report',
    path: '/detailedPayrollReports',
    sidebar: 'Payroll → Detailed Payroll',
    access_points: ['detailedPayrollReports'],
  },
]

// ─── Full payroll setup flow guide ────────────────────────────────────────────

export const PAYROLL_FLOW_GUIDE = `**Payroll Setup — Full Flow**

Follow these steps in order to get payroll running correctly.

---

**Step 1 — Define Allowances** *(Payroll → Allowances)*
Create all allowance types your organisation uses (e.g. Housing, Transport, Meal).
Each allowance has a name, type (fixed / percentage), and taxability flag.

**Step 2 — Define Deductions** *(Payroll → Deductions)*
Create all deduction types (e.g. Tax, Pension, Loan, Insurance).
Each deduction has a name, type (fixed / percentage), and calculation basis.

**Step 3 — Create Payroll Profile** *(Payroll → Payroll Profile)*
A Payroll Profile bundles together:
- Base salary rules
- Which allowances and deductions apply
- Overtime calculation method
- Payment frequency (monthly, bi-weekly, etc.)

**Step 4 — Map Profile to Employees or Designations**
- *Employee Payroll* (Payroll → Employee Payroll) — assign a profile to individual employees and set their base salary.
- *Designation Profile* (Payroll → Designation Profile) — assign a profile to an entire designation so all employees in that designation inherit it automatically.

**Step 5 — Run Payroll Recalculation** *(Payroll → Payroll Recalculation)*
Select the period (month/year) and trigger recalculation.
The system computes net pay for each employee based on attendance, overtime, allowances, and deductions.

**Step 6 — Review & Generate Payslips** *(Payroll → Payslip)*
Review computed payslips per employee.
Generate and distribute payslips individually or in bulk.

**Step 7 — Reports**
- *Detailed Payroll Report* — full breakdown per employee
- *Extended Payroll Report* — includes visa, mode, and extra fields
- *OT Verification* — verify overtime before finalising payroll`

// ─── Per-page guide texts (keyed by PAYROLL_ITEMS key) ────────────────────────

export const PAYROLL_GUIDES = {
  allowances: `**Allowances**
📍 *Sidebar: Payroll → Allowances*

Define the types of allowances available in your organisation.

**Fields:**
- Name (e.g. Housing, Transport, Meal)
- Type: Fixed amount or Percentage of base salary
- Taxable: Whether this allowance is subject to tax

**Note:** Allowances are referenced when creating a Payroll Profile.`,

  deductions: `**Deductions**
📍 *Sidebar: Payroll → Deductions*

Define the types of deductions that can be applied to employee salaries.

**Fields:**
- Name (e.g. Tax, Pension, Insurance, Loan)
- Type: Fixed amount or Percentage
- Calculation basis (gross pay, basic salary, etc.)

**Note:** Deductions are referenced when creating a Payroll Profile.`,

  payroll_profile: `**Payroll Profile**
📍 *Sidebar: Payroll → Payroll Profile*

The central configuration that defines how salary is calculated for a group of employees.

**Includes:**
- Basic salary rules
- Allowances to include (from the Allowances list)
- Deductions to apply (from the Deductions list)
- Overtime calculation method
- Payment frequency

**After creating a profile**, map it to employees via *Employee Payroll* or to a designation via *Designation Profile*.`,

  employee_payroll: `**Employee Payroll**
📍 *Sidebar: Payroll → Employee Payroll*

Map individual employees to a Payroll Profile and set their specific salary details.

**Fields per employee:**
- Select employee
- Select Payroll Profile
- Base salary (overrides profile default if needed)
- Effective date

**Tip:** Use *Designation Profile* to map an entire designation at once instead of individual employees.`,

  designation_payroll: `**Designation Profile**
📍 *Sidebar: Payroll → Designation Profile*

Assign a Payroll Profile to a designation so all employees in that designation are automatically covered.

**Fields:**
- Select designation
- Select Payroll Profile
- Default base salary for the designation

**Tip:** Employee-level mapping in *Employee Payroll* overrides the designation mapping for that specific employee.`,

  payslip: `**Payslip**
📍 *Sidebar: Payroll → Payslip*

View, verify, and distribute payslips generated after payroll recalculation.

**Actions:**
- Filter by month, year, branch, or employee
- View detailed breakdown (earnings, deductions, net pay)
- Generate PDF payslips
- Send payslips by email (if email is configured)`,

  payroll_recalculation: `**Payroll Recalculation**
📍 *Sidebar: Payroll → Payroll Recalculation*

Trigger payroll computation for a selected period.

**Steps:**
1. Select month and year
2. Choose scope: all employees, specific branch, or specific employees
3. Click Recalculate
4. Review status — employees with errors or missing profiles are flagged

**Run recalculation after** any attendance corrections or payroll profile changes for that period.`,

  detailed_payroll: `**Detailed Payroll Report**
📍 *Sidebar: Payroll → Detailed Payroll*

Full per-employee salary breakdown for a selected period.

**Shows:**
- Basic salary, allowances, deductions
- Overtime amount
- Absent / late deductions
- Net pay

**Filters:** Month, year, branch, designation, employee`,
}

// ─── Payroll issue diagnostic wizard steps ────────────────────────────────────
export const PAYROLL_ISSUE_STEPS = [
  {
    key: 'employee_id',
    label: 'Enter the **Employee ID** of the employee with the payroll issue:',
  },
  {
    key: 'issue_field',
    label: 'Which field looks wrong? Select one:',
    options: [
      { label: 'Pay Start Date', value: 'pay_start_date' },
      { label: 'Pay End Date', value: 'pay_end_date' },
      { label: 'Basic Salary', value: 'basic_salary' },
      { label: 'Total Allowances', value: 'total_allowances' },
      { label: 'Total Deductions', value: 'total_deductions' },
      { label: 'Fixed Allowance', value: 'fixed_allowance' },
      { label: 'Fixed Deduction', value: 'fixed_deduction' },
      { label: 'Days in Period', value: 'days_in_period' },
      { label: 'Working Days', value: 'working_days' },
      { label: 'Absent Days', value: 'absent_days' },
      { label: 'Leave Days', value: 'leave_days' },
      { label: 'Weekend Days', value: 'weekend_days' },
      { label: 'Gross Salary', value: 'gross_salary' },
      { label: 'Total Pay', value: 'total_pay' },
      { label: 'Leave Without Pay (LWP)', value: 'lwp_days' },
    ],
  },
  {
    key: 'month_year',
    label: 'For which **Month/Year**? Enter as `YYYY-MM` (e.g. `2026-05`):',
  },
]
